import { AibLineType, AsupInternalBlock } from "aic";
import { v3EditorProps } from "components/common/v3EditorProps";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { HierarchyContext } from "contexts/HierarchyContext";
import { getExternalSingles } from "contexts/tlf/getExternalSingles";
import { TlfContext } from "contexts/TlfContext";
import { IEditorV3 } from "editor-v3";
import { ReactNode, useContext } from "react";
import { UPDATE_TLF } from "../../contexts/tlf/interface";
import { eHierarchyLevel } from "../../functions/api/enums";
import { CommentScope } from "../common/comments/CommentScope";

interface TitlesProps {
  firstLine?: number;
  lastLine?: number;
  noComments?: boolean;
  patch?: boolean;
}

/**
 * Render TLF titles block with optional slicing and comment support.
 *
 * @param props - Destructured properties for titles.
 * @param props.firstLine - 1-based index of the first title line to include.
 * @param props.lastLine - 1-based index of the last title line to include.
 * @param props.noComments - When true, disables comment UI within the block.
 * @param props.patch - When true, persist changes via patch API instead of context dispatch.
 * @returns React node rendering the titles editor block.
 */
export const Titles = ({
  firstLine = 1,
  lastLine = 10,
  noComments = false,
  patch = false,
}: TitlesProps): ReactNode => {
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const tlfContext = useContext(TlfContext);
  const tlfData = tlfContext?.tlfData;
  const patchTlf = tlfContext?.patchTlf;

  const externalSingles = getExternalSingles(
    tlfData?.tocNumber,
    tlfData?.releaseStatus,
    tlfData?.domain,
    tlfData?.singles,
    hierarchyContext?.hierarchy?.level,
    hierarchyContext?.hierarchy?.singles,
    hierarchyContext?.hierarchy?.domainReplacements,
    hierarchyContext?.parentTemplates.singles,
    hierarchyContext?.parentTemplates.domainReplacements,
  );

  return !hierarchyContext || !tlfData ? null : (
    <CommentScope
      id="titles-comment"
      activeContext="TlfItem"
      commentLabel="New titles comment"
      disabled={noComments}
    >
      <AsupInternalBlock<IEditorV3>
        id="titles"
        lineStyle={appSettings.defaultTableStyle}
        lines={tlfData.titles.slice(firstLine - 1, lastLine)}
        disabled={tlfContext.readOnly}
        onChange={(ret) => {
          const newTitles = [
            ...tlfData.titles.slice(0, firstLine - 1),
            ...ret,
            ...tlfData.titles.slice(lastLine),
          ];
          if (patch && patchTlf) {
            patchTlf("titles", newTitles);
          } else {
            tlfContext.dispatch({
              operation: UPDATE_TLF,
              tlfData: { titles: newTitles },
              calledFrom: "Titles:Update",
            });
          }
        }}
        externalSingles={externalSingles}
        minLines={5 - firstLine}
        defaultType={AibLineType.centerOnly}
        editorProps={v3EditorProps}
        noComments={noComments}
        noTrafficLights={
          !tlfContext.showTrafficLights ||
          tlfData.primaryHierarchyLevel !== eHierarchyLevel.Report ||
          tlfData.coreTrafficLightSwitch === "DoNotCalculate"
        }
        changedBlocks={tlfData.changedTitles}
      />
    </CommentScope>
  );
};

Titles.displayName = "Titles";
